import React from 'react';
import styled from 'styled-components';
import { useCart } from '../../contexts/CartContext';
import { getImageUrl } from '../../utils/apiUtils';

// Format price with currency
const formatPrice = (price, currency = 'USD') => {
  if (price === null || price === undefined) return 'Price not available';
  if (price === 0) return 'Free';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(price);
};

const CartItemContainer = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: ${props => props.theme.colors.card};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.radii.md};
  transition: all 0.2s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${props => props.theme.shadows.glowPrimary};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const ItemImage = styled.div`
  width: 80px;
  height: 80px;
  background: ${props => props.theme.colors.bg};
  border-radius: ${props => props.theme.radii.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.textDim};
  font-size: 2rem;
  flex-shrink: 0;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: ${props => props.theme.radii.sm};
  }
`;

const ItemDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ItemName = styled.h4`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  line-height: 1.3;
`;

const ItemPrice = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: #059669;
`;

const ItemSeller = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme.colors.textDim};
`;

const ItemControls = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
  position: relative;
`;

const RemoveButton = styled.button`
  background: #ef4444;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 600;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 80px;
  justify-content: center;

  &:hover {
    background: #dc2626;
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus {
    outline: 2px solid #ef4444;
    outline-offset: 2px;
  }
`;

const ItemTotal = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  text-align: right;
`;

const CartItem = ({ item }) => {
  const { removeFromCart } = useCart();

  const handleRemove = () => {
    // Add confirmation for removing items
    if (window.confirm(`Are you sure you want to remove "${item.productName}" from your cart?`)) {
      removeFromCart(item.productId);
    }
  };

  return (
    <CartItemContainer>
      <ItemImage>
        {item.image ? (
          <img 
            src={getImageUrl(item.productId, item.image._id)}
            alt={item.productName}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          />
        ) : null}
        <div style={{ display: item.image ? 'none' : 'block' }}>
          📦
        </div>
      </ItemImage>
      
      <ItemDetails>
        <ItemName>{item.productName}</ItemName>
        <ItemPrice>{formatPrice(item.price, item.currency || 'USD')}</ItemPrice>
        <ItemSeller>by {item.sellerName}</ItemSeller>
      </ItemDetails>
      
      <ItemControls>
        <ItemTotal>
          {formatPrice(item.price, item.currency || 'USD')}
        </ItemTotal>
        
        <RemoveButton 
          onClick={handleRemove}
          title="Remove item from cart"
          aria-label="Remove item from cart"
        >
          🗑️ Remove
        </RemoveButton>
      </ItemControls>
    </CartItemContainer>
  );
};

export default CartItem;
